// Copyright © 2026 Alex Bruce. All rights reserved.
//
// Binds a piece of React state to one of the org's shared libraries.
//
// Returns a setter with the same signature as the useState one it replaces,
// so the Settings UI does not change: every existing call site keeps working
// and gains persistence. Writing the wiring five times by hand is how the
// account menu ended up with three copies.

import { useCallback, useEffect, useRef } from "react";
import { loadOrgSetting, saveOrgSetting, type OrgSettingKey, type SaveOutcome } from "../lib/orgSettings";

interface Options<T> {
  /** Milliseconds of quiet before a save. Typing in a name field fires per keystroke. */
  debounceMs?: number;
  /** Shape the value for storage -- for a per-org slice of a wider map. */
  toStored?: (value: T) => unknown;
  /** Fold a stored value back into local state. */
  fromStored?: (stored: any, current: T) => T;
}

export function useOrgSetting<T>(
  orgId: string | null,
  userId: string | null,
  key: OrgSettingKey,
  value: T,
  setValue: React.Dispatch<React.SetStateAction<T>>,
  onError: (message: string) => void,
  opts: Options<T> = {},
): React.Dispatch<React.SetStateAction<T>> {
  const { debounceMs = 800, toStored, fromStored } = opts;

  // The timestamp we last saw, so a save can tell a colleague's change from
  // our own. Null means "no row yet".
  const stampRef = useRef<string | null>(null);
  const loadedRef = useRef<string | null>(null);
  const timerRef = useRef<any>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!orgId || loadedRef.current === `${orgId}:${key}`) return;
    loadedRef.current = `${orgId}:${key}`;
    let cancelled = false;
    (async () => {
      try {
        const { value: stored, updatedAt } = await loadOrgSetting<any>(orgId, key);
        if (cancelled) return;
        stampRef.current = updatedAt;
        if (stored === null || stored === undefined) {
          // Nothing saved yet: publish what this admin already has, so a new
          // org inherits a working set rather than starting empty. A plain
          // member's write is refused by RLS, which is not worth a toast --
          // they simply keep reading whatever an admin later publishes.
          if (!userId) return;
          const out: SaveOutcome<any> = await saveOrgSetting<any>(
            orgId, key, toStored ? toStored(valueRef.current) : valueRef.current,
            null, userId,
          );
          if (out.ok) stampRef.current = out.updatedAt;
          return;
        }
        setValue(prev => (fromStored ? fromStored(stored, prev) : (stored as T)));
      } catch (e: any) {
        if (!cancelled) onErrorRef.current(`Could not load shared ${key.replace(/_/g, " ")}: ${e.message || e}`);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, key, userId]);

  return useCallback<React.Dispatch<React.SetStateAction<T>>>(next => {
    setValue(prev => {
      const resolved = typeof next === "function"
        ? (next as (p: T) => T)(prev) : next;
      if (!orgId || !userId) return resolved;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        try {
          const out: SaveOutcome<any> = await saveOrgSetting<any>(
            orgId, key, toStored ? toStored(resolved) : resolved,
            stampRef.current, userId,
          );
          if (out.ok) { stampRef.current = out.updatedAt; return; }
          if (out.conflict) {
            // Someone else saved while this was being edited. Take theirs
            // rather than clobber it, and say so -- a silent overwrite of a
            // colleague's labour rates is the failure worth avoiding.
            stampRef.current = out.updatedAt;
            if (out.theirs != null) {
              setValue(p => (fromStored ? fromStored(out.theirs, p) : (out.theirs as T)));
            }
            onErrorRef.current(`Someone else updated the shared ${key.replace(/_/g, " ")}; showing their version.`);
            return;
          }
          onErrorRef.current(out.error);
        } catch (e: any) {
          onErrorRef.current(e.message || String(e));
        }
      }, debounceMs);

      return resolved;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, userId, key, debounceMs]);
}
